import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { addDays, fmtDay, fmtTime, todayIso, TIMES } from "@/lib/scheduling";

export const SlotPicker = ({ caseId, onPick, picked, exclude, emptyContent, testPrefix = "slot" }) => {
  const today = todayIso();
  const [from, setFrom] = useState(today);
  const [day, setDay] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["scheduling", "slots", caseId, from, exclude || null],
    queryFn: async () => (await api.get("/scheduling/slots", { params: { case_id: caseId, from, days: 14, exclude: exclude || undefined } })).data,
  });
  const byDay = useMemo(() => (data?.slots || []).reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {}), [data]);
  const dates = Object.keys(byDay);
  const key = dates.join("|");
  useEffect(() => { if (dates.length && !byDay[day]) setDay(dates[0]); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const shift = (n) => { setFrom((f) => { const next = addDays(f, n); return next < today ? today : next; }); setDay(null); };
  const until = addDays(from, 13);

  return (
    <div data-testid={`${testPrefix}-picker`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" className="btn-quiet min-h-[40px]" disabled={from <= today} onClick={() => shift(-14)} data-testid={`${testPrefix}-earlier`}><ChevronLeft className="h-4 w-4" aria-hidden="true" /> Earlier</button>
        <p className="font-serif text-lg font-semibold" aria-live="polite" data-testid={`${testPrefix}-range`}>{fmtDay(from, { month: "long", day: "numeric" })} – {fmtDay(until, { month: "long", day: "numeric" })}</p>
        <button type="button" className="btn-quiet min-h-[40px]" onClick={() => shift(14)} data-testid={`${testPrefix}-later`}>Later <ChevronRight className="h-4 w-4" aria-hidden="true" /></button>
      </div>
      {isLoading ? <p className="mt-4 text-ink-500" role="status">Finding open times…</p> : dates.length === 0 ? (
        <div className="mt-4" data-testid={`${testPrefix}-empty`}>{emptyContent || <p className="text-ink-500">No open times in these two weeks. Try a later range.</p>}</div>
      ) : (
        <>
          <div role="tablist" aria-label="Days with open times" className="mt-4 flex gap-2 overflow-x-auto pb-2">
            {dates.map((d) => (
              <button key={d} type="button" role="tab" aria-selected={d === day} onClick={() => setDay(d)} className={`min-w-[92px] shrink-0 border px-3 py-2 text-left transition-colors ${d === day ? "border-olive-900 bg-olive-900 text-cream-50" : "border-sage-400 bg-cream-100 hover:border-olive-900"}`} data-testid={`${testPrefix}-day-${d}`}>
                <span className="block text-xs uppercase tracking-wide opacity-80">{fmtDay(d, { weekday: "short" })}</span>
                <span className="block font-serif text-lg font-semibold">{fmtDay(d, { month: "short", day: "numeric" })}</span>
                <span className="block text-xs opacity-80">{byDay[d].length} time{byDay[d].length === 1 ? "" : "s"}</span>
              </button>
            ))}
          </div>
          {day && byDay[day] && (
            <div className="mt-4 grid gap-5 sm:grid-cols-3" role="tabpanel">
              {TIMES.map(([bucket, label]) => {
                const list = byDay[day].filter((s) => s.bucket === bucket);
                if (!list.length) return null;
                return (
                  <div key={bucket}>
                    <p className="label text-sm">{label}</p>
                    <div className="flex flex-wrap gap-2">
                      {list.map((s, i) => {
                        const on = picked?.start_at === s.start_at;
                        return (
                          <button key={s.start_at} type="button" aria-pressed={on} onClick={() => onPick(s)} className={`min-h-[44px] border px-3 py-1.5 text-left text-sm transition-colors ${on ? "border-olive-900 bg-olive-900 text-cream-50" : "border-sage-400 bg-cream-100 hover:border-olive-900"}`} data-testid={`${testPrefix}-time-${bucket}-${i}`}>
                            <span className="block font-semibold">{fmtTime(s.start_at)}</span><span className="block text-xs opacity-80">{s.location}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
